import { MULTIRX_LOGO_SRC } from './multirxHero.js';

/** Durée du fondu entrée / sortie (ms). */
export const FEATURED_PILOT_TRANSITION_FADE_MS = 280;
/** Durée du balayage reflet (ms). */
export const FEATURED_PILOT_TRANSITION_SHINE_MS = 560;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function waitImageReady(img: HTMLImageElement): Promise<void> {
  if (img.complete && img.naturalWidth > 0) return Promise.resolve();
  return new Promise((resolve) => {
    const done = (): void => {
      img.removeEventListener('load', done);
      img.removeEventListener('error', done);
      resolve();
    };
    img.addEventListener('load', done);
    img.addEventListener('error', done);
  });
}

function buildOverlay(): {
  overlay: HTMLElement;
  logo: HTMLImageElement;
} {
  const overlay = document.createElement('div');
  overlay.className = 'featured-pilot-transition';
  overlay.setAttribute('aria-hidden', 'true');

  const veil = document.createElement('div');
  veil.className = 'featured-pilot-transition__veil';

  const wrap = document.createElement('div');
  wrap.className = 'featured-pilot-transition__logo-wrap';
  wrap.style.setProperty('--featured-pilot-logo-mask', `url("${MULTIRX_LOGO_SRC}")`);

  const logo = document.createElement('img');
  logo.className = 'featured-pilot-transition__logo';
  logo.src = MULTIRX_LOGO_SRC;
  logo.alt = '';
  logo.draggable = false;

  const shine = document.createElement('span');
  shine.className = 'featured-pilot-transition__shine';
  shine.setAttribute('aria-hidden', 'true');

  const beam = document.createElement('span');
  beam.className = 'featured-pilot-transition__shine-beam';
  shine.appendChild(beam);

  wrap.append(logo, shine);
  overlay.append(veil, wrap);
  return { overlay, logo };
}

/**
 * Overlay gris + logo en fondu, reflet type lame (masqué au logo).
 * Le swap se fait sous le voile (invisible), puis shine, puis fondu sortie.
 */
export async function playFeaturedPilotTransition(
  host: HTMLElement,
  onSwap: () => void | Promise<void>
): Promise<void> {
  if (prefersReducedMotion()) {
    await onSwap();
    return;
  }

  host.querySelector('.featured-pilot-transition')?.remove();
  const { overlay, logo } = buildOverlay();
  host.appendChild(overlay);

  await waitImageReady(logo);
  if (!overlay.isConnected) {
    await onSwap();
    return;
  }

  // Force un frame à opacity 0 avant d’ouvrir.
  void overlay.getBoundingClientRect();
  overlay.classList.add('is-visible');

  await sleep(FEATURED_PILOT_TRANSITION_FADE_MS);
  if (!overlay.isConnected) {
    await onSwap();
    return;
  }

  // Contenu changé sous le gris : invisible pour l’utilisateur.
  // Shine en parallèle ; on attend les deux avant de révéler.
  overlay.classList.add('is-shining');
  await Promise.all([onSwap(), sleep(FEATURED_PILOT_TRANSITION_SHINE_MS)]);

  if (!overlay.isConnected) return;

  overlay.classList.remove('is-visible', 'is-shining');
  overlay.classList.add('is-leaving');
  await sleep(FEATURED_PILOT_TRANSITION_FADE_MS);
  overlay.remove();
}
